import React from 'react';
import { Chip, useMediaQuery, useTheme } from '@mui/material';
import { AccountBalanceWallet as WalletIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useBilling } from '../../hooks/useBilling';

/**
 * AppBar balance chip for the External portal (new IA, plan §4 shell): "N analyses". Filled primary
 * when the user has credits, outlined when at zero. Click → checkout at zero, else Profile › Billing.
 * Mounted by AppBar only when Layout says `showAnalysesChip` (External + billingEnabled), so the
 * summary fetch inside useBilling never runs for anyone else. Renders nothing while the balance is
 * unavailable (fetch error) — Home carries the retry affordance.
 */
export const AnalysesChip: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const { balance, loading, loaded, error } = useBilling();
  if (error && !loaded) return null;
  const hasCredits = balance > 0;
  const label = loading && !loaded ? '… analyses' : `${balance} ${balance === 1 ? 'analysis' : 'analyses'}`;

  return (
    <Chip
      icon={<WalletIcon />}
      label={label}
      size={compact ? 'small' : 'medium'}
      color={hasCredits ? 'primary' : 'default'}
      variant={hasCredits ? 'filled' : 'outlined'}
      onClick={() => navigate(hasCredits ? '/portal/profile?tab=billing' : '/portal/buy')}
      aria-label={`${label} — ${hasCredits ? 'billing' : 'buy analyses'}`}
      sx={{ fontWeight: 600, mr: { xs: 0.5, sm: 1 } }}
    />
  );
};

export default AnalysesChip;
